import type {
    JSA_ARCHIVE_TILE_TYPE,
    JSA_CHANGELOG_TILE_TYPE,
    JSA_CLUSTER_DETAIL_TILE_TYPE,
    JSA_COMPARE_TILE_TYPE,
    JSA_FAMILY_DETAIL_TILE_TYPE,
    JSA_PROFILE_DETAIL_TILE_TYPE,
} from '~/feature/job-architecture-v2';
import type {
    LEARNING_OPPORTUNITY_CREATE_TILE_TYPE,
    LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE,
    LEARNING_OPPORTUNITY_EDIT_TILE_TYPE,
} from '~/feature/learning-opportunities-v2';
import type { SKILL_DETAIL_TILE_TYPE } from '~/feature/skills';

import type { TileAvailability, TileRoutePolicy } from '../url-tiles';
import { collectTileRoutePolicies } from '../url-tiles';
import { lazyRoutesConfig } from './lazy-routes-config';
import { route as dashboardV2Route } from './pages/dashboard-v2/constants';

export type RegisteredUrlTileType =
    | typeof JSA_ARCHIVE_TILE_TYPE
    | typeof JSA_CHANGELOG_TILE_TYPE
    | typeof JSA_CLUSTER_DETAIL_TILE_TYPE
    | typeof JSA_COMPARE_TILE_TYPE
    | typeof JSA_FAMILY_DETAIL_TILE_TYPE
    | typeof JSA_PROFILE_DETAIL_TILE_TYPE
    | typeof LEARNING_OPPORTUNITY_CREATE_TILE_TYPE
    | typeof LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE
    | typeof LEARNING_OPPORTUNITY_EDIT_TILE_TYPE
    | typeof SKILL_DETAIL_TILE_TYPE;

export type TilePermissionKey = 'createLearningOpportunities' | 'manageLearningOpportunities';

type AppTileAvailability = TileAvailability & {
    readonly permission?: TilePermissionKey;
    readonly type: RegisteredUrlTileType;
};

type AppTileRoutePolicy = {
    readonly pattern: string;
    readonly tiles: readonly AppTileAvailability[];
};

const browserRouterTileRoutePolicies: readonly AppTileRoutePolicy[] = [
    {
        pattern: '/job-architecture-v2/:id/*',
        tiles: [
            { type: 'v1.jsa.archive' },
            { type: 'v1.jsa.changelog' },
            { type: 'v1.jsa.cluster.detail' },
            { type: 'v1.jsa.compare' },
            { type: 'v1.jsa.family.detail' },
            { type: 'v1.jsa.profile.detail' },
            { type: 'v1.skill.detail' },
        ],
    },
    {
        pattern: '/job-profile/:architectureId/:itemType/:itemId',
        tiles: [{ type: 'v1.jsa.compare' }, { type: 'v1.skill.detail' }],
    },
    {
        pattern: '/skills/*',
        tiles: [{ type: 'v1.jsa.profile.detail' }, { type: 'v1.skill.detail' }],
    },
    {
        pattern: '/learning-opportunities-v2/*',
        tiles: [
            { permission: 'createLearningOpportunities', type: 'v1.learning-opportunity.create' },
            { permission: 'manageLearningOpportunities', type: 'v1.learning-opportunity.detail' },
            { permission: 'manageLearningOpportunities', type: 'v1.learning-opportunity.edit' },
        ],
    },
    {
        pattern: dashboardV2Route.patterns.dashboardV2,
        tiles: [{ type: 'v1.jsa.profile.detail' }, { type: 'v1.skill.detail' }],
    },
    {
        pattern: '/home-2',
        tiles: [
            { type: 'v1.jsa.archive' },
            { type: 'v1.jsa.changelog' },
            { type: 'v1.jsa.cluster.detail' },
            { type: 'v1.jsa.compare' },
            { type: 'v1.jsa.family.detail' },
            { type: 'v1.jsa.profile.detail' },
            { type: 'v1.skill.detail' },
        ],
    },
];

export const appTileRoutePolicies: readonly TileRoutePolicy[] = collectTileRoutePolicies({
    explicitPolicies: browserRouterTileRoutePolicies,
    routeDefinitions: lazyRoutesConfig,
});
